import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '../../../../components/Layout';
import paymentTypeService, { UpdatePaymentTypeDTO, PaymentCategory, PaymentUsageScope } from '../../../../services/paymentTypeService';
import '../../../../styles/ImsTheme.css';
import '../../../../pages/Products/ProductCategoryEditPage.css'; // shared edit-* / form-* / info-* structure
import './PaymentTypeFormPage.css';               // ptf-category-card* / pt-cat-badge* styles
import { ArrowLeft, Save } from 'lucide-react';
import { parseApiError } from '../../../../utils/errorUtils';

// ─── Category radio card options ─────────────────────────────────────────────

const SCOPE_OPTIONS: { value: PaymentUsageScope; icon: string; label: string; hint: string; color: string }[] = [
  { value: 'Sales',    icon: '🛒', label: 'Sales',    color: '#15803d', hint: 'Appears only in sales invoices and POS.' },
  { value: 'Purchase', icon: '📦', label: 'Purchase', color: '#1d4ed8', hint: 'Appears only in Purchase Orders and GRN.' },
  { value: 'Both',     icon: '🔄', label: 'Both',     color: '#7c3aed', hint: 'Appears in both sales and purchasing flows.' },
];

const CATEGORY_OPTIONS: {
  value: PaymentCategory;
  icon: string;
  label: string;
  hint: string;
  color: string;
}[] = [
  { value: 'Cash',   icon: '💵', label: 'Cash',   color: '#15803d', hint: 'Physical cash payments. Only one Cash type allowed.'            },
  { value: 'Credit', icon: '🏦', label: 'Credit', color: '#1d4ed8', hint: 'Customer on-account credit. Only one Credit type allowed.'       },
  { value: 'Other',  icon: '💳', label: 'Other',  color: '#7c3aed', hint: 'Card, voucher, bank transfer, etc. Multiple types allowed.'      },
];

// ─── Component ────────────────────────────────────────────────────────────────

const PaymentTypeEditPage: React.FC = () => {
  const { id }    = useParams<{ id: string }>();
  const navigate  = useNavigate();
  const { t }     = useTranslation();

  const [loading,      setLoading]      = useState(false);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [error,        setError]        = useState<string | null>(null);

  // Singleton categories taken by OTHER payment types (not this one)
  const [takenByOthers, setTakenByOthers] = useState<Set<string>>(new Set());

  const [formData, setFormData] = useState<UpdatePaymentTypeDTO>({
    code:            '',
    name:            '',
    description:     '',
    paymentCategory: 'Other',
    usageScope:      'Both',
    isDefault:       false,
    isActive:        true,
    sortOrder:       0,
  });

  useEffect(() => { loadPaymentType(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadPaymentType = async () => {
    setFetchLoading(true);
    try {
      const [data, allActive] = await Promise.all([
        paymentTypeService.getPaymentTypeById(parseInt(id || '0')),
        paymentTypeService.getActivePaymentTypes(),
      ]);

      setFormData({
        code:            data.code,
        name:            data.name,
        description:     data.description || '',
        paymentCategory: data.paymentCategory ?? 'Other',
        usageScope:      data.usageScope ?? 'Both',
        isDefault:       data.isDefault,
        isActive:        data.isActive,
        sortOrder:       data.sortOrder,
      });

      // Which singleton categories are taken by OTHER payment types?
      const thisId = parseInt(id || '0');
      const taken  = new Set(
        allActive
          .filter((pt) => pt.id !== thisId &&
            (pt.paymentCategory === 'Cash' || pt.paymentCategory === 'Credit'))
          .map((pt) => pt.paymentCategory)
      );
      setTakenByOthers(taken);
    } catch {
      setError(t('paymentType.failedToLoad'));
    } finally {
      setFetchLoading(false);
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value,
    }));
  };

  const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({ ...prev, sortOrder: parseInt(e.target.value) || 0 }));
  };

  const validate = (): boolean => {
    if (!formData.code.trim()) { setError(t('paymentType.codeRequired')); return false; }
    if (!formData.name.trim()) { setError(t('paymentType.nameRequired')); return false; }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!validate()) return;

    setLoading(true);
    try {
      await paymentTypeService.updatePaymentType(parseInt(id || '0'), formData);
      navigate(`/settings/payment-types/${id}`, {
        state: { message: t('paymentType.updateSuccess') },
      });
    } catch (err: unknown) {
      setError(parseApiError(err, t('paymentType.failedToUpdate')).message);
      setLoading(false);
    }
  };

  // ── Loading state ──────────────────────────────────────────────────────────

  if (fetchLoading) {
    return (
      <Layout>
        <div className="edit-loading-container">
          <div className="edit-spinner-border" role="status">
            <span className="visually-hidden">{t('common.loading')}</span>
          </div>
        </div>
      </Layout>
    );
  }

  // ── Category badge helper ──────────────────────────────────────────────────

  const catBadgeCls: Record<string, string> = {
    Cash: 'info-badge info-badge-active',
    Credit: 'info-badge info-badge-sub',
    Other: 'info-badge info-badge-root',
  };

  // ─────────────────────────────────────────────────────────────────────────────

  return (
    <Layout>
      <div className="edit-container">

        <div className="ims-top-bar">
          <div className="ims-top-bar-left">
            <button type="button" className="ims-back-btn" onClick={() => navigate('/settings/payment-types')}>
              <ArrowLeft size={16} strokeWidth={2} />
            </button>
            <div className="ims-page-title">
              {t('paymentType.editTitle')}
              <span className="ims-page-subtitle">{formData.name}</span>
            </div>
          </div>
          <div className="ims-top-bar-right">
            <button type="button" className="ims-btn ims-btn-secondary" onClick={() => navigate(`/settings/payment-types/${id}`)} disabled={loading}>
              {t('common.cancel')}
            </button>
            <button type="submit" form="page-form" className="ims-btn ims-btn-primary" disabled={loading}>
              {loading ? <><span className="ims-btn-spinner" />{t('common.saving')}</> : <><Save size={15} strokeWidth={2} />{t('common.saveChanges')}</>}
            </button>
          </div>
        </div>

        {/* ── Error alert ── */}
        {error && (
          <div className="alert alert-danger alert-dismissible">
            {error}
            <button
              style={{ background: 'none', border: 'none', cursor: 'pointer', fontWeight: 700 }}
              onClick={() => setError(null)}
            >×</button>
          </div>
        )}

        {/* ── Two-column content ── */}
        <div className="edit-content">

          {/* ════ LEFT — form card ════ */}
          <div className="edit-form-section">
            <div className="edit-card">
              <div className="edit-card-header">
                <h2 className="edit-card-title">{t('paymentType.details')}</h2>
              </div>
              <div className="edit-card-body">
                <form id="page-form" onSubmit={handleSubmit} noValidate>

                  {/* ── Usage Scope ── */}
                  <div className="form-group">
                    <label className="form-label">
                      {t('paymentType.usageScope', 'Usage Scope')} <span className="required">*</span>
                    </label>
                    <div className="ptf-category-cards">
                      {SCOPE_OPTIONS.map((opt) => {
                        const selected = formData.usageScope === opt.value;
                        return (
                          <label
                            key={opt.value}
                            className={['ptf-category-card', selected ? 'ptf-category-card--active' : ''].join(' ')}
                            style={{ '--cat-color': opt.color } as React.CSSProperties}
                          >
                            <input
                              type="radio"
                              name="usageScope"
                              value={opt.value}
                              checked={selected}
                              onChange={() => setFormData((p) => ({ ...p, usageScope: opt.value }))}
                              className="ptf-category-radio"
                            />
                            <span className="ptf-category-icon">{opt.icon}</span>
                            <div className="ptf-category-body">
                              <span className="ptf-category-label">{opt.label}</span>
                              <span className="ptf-category-hint">{opt.hint}</span>
                            </div>
                            {selected && <span className="ptf-category-check">✓</span>}
                          </label>
                        );
                      })}
                    </div>
                  </div>

                  {/* ── Payment Category ── */}
                  <div className="form-group">
                    <label className="form-label">
                      {t('paymentType.paymentCategory')}
                      {(formData.paymentCategory === 'Cash' || formData.paymentCategory === 'Credit') && (
                        <span className="ptf-singleton-badge">🔒 Singleton</span>
                      )}
                    </label>
                    <div className="ptf-category-cards">
                      {CATEGORY_OPTIONS.map((opt) => {
                        const selected = formData.paymentCategory === opt.value;
                        const disabled = takenByOthers.has(opt.value);
                        return (
                          <label
                            key={opt.value}
                            className={[
                              'ptf-category-card',
                              selected ? 'ptf-category-card--active'   : '',
                              disabled ? 'ptf-category-card--disabled' : '',
                            ].join(' ')}
                            style={{ '--cat-color': opt.color } as React.CSSProperties}
                          >
                            <input
                              type="radio"
                              name="paymentCategory"
                              value={opt.value}
                              checked={selected}
                              disabled={disabled}
                              onChange={() =>
                                !disabled &&
                                setFormData((p) => ({ ...p, paymentCategory: opt.value }))
                              }
                              className="ptf-category-radio"
                            />
                            <span className="ptf-category-icon">{opt.icon}</span>
                            <div className="ptf-category-body">
                              <span className="ptf-category-label">{opt.label}</span>
                              <span className="ptf-category-hint">
                                {disabled
                                  ? `⚠️ Another active ${opt.label} type already exists.`
                                  : opt.hint}
                              </span>
                            </div>
                            {disabled  && <span className="ptf-category-locked">🔒</span>}
                            {!disabled && selected && <span className="ptf-category-check">✓</span>}
                          </label>
                        );
                      })}
                    </div>
                  </div>

                  {/* ── Code ── */}
                  <div className="form-group">
                    <label className="form-label">
                      {t('paymentType.code')}
                      <span className="char-counter">{formData.code.length}/10</span>
                    </label>
                    <input
                      type="text"
                      className="form-input"
                      name="code"
                      value={formData.code}
                      onChange={handleInputChange}
                      maxLength={10}
                      style={{ textTransform: 'uppercase' }}
                    />
                    <small className="form-helper">{t('paymentType.codeHelper')}</small>
                  </div>

                  {/* ── Name ── */}
                  <div className="form-group">
                    <label className="form-label">
                      {t('paymentType.name')} <span className="required">*</span>
                      <span className="char-counter">{formData.name.length}/20</span>
                    </label>
                    <input
                      type="text"
                      className="form-input"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      maxLength={20}
                      placeholder={t('paymentType.namePlaceholder')}
                    />
                  </div>

                  {/* ── Description ── */}
                  <div className="form-group">
                    <label className="form-label">
                      {t('common.description')}
                      <span className="char-counter">{(formData.description || '').length}/200</span>
                    </label>
                    <textarea
                      className="form-textarea"
                      name="description"
                      value={formData.description || ''}
                      onChange={handleInputChange}
                      rows={3}
                      maxLength={200}
                      placeholder={t('paymentType.descriptionPlaceholder')}
                    />
                  </div>

                  {/* ── Sort order ── */}
                  <div className="form-group">
                    <label className="form-label">{t('paymentType.sortOrder')}</label>
                    <input
                      type="number"
                      className="form-input"
                      value={formData.sortOrder}
                      onChange={handleNumberChange}
                      min={0}
                      max={9999}
                      style={{ maxWidth: 120 }}
                    />
                    <small className="form-helper">{t('paymentType.sortOrderHelper')}</small>
                  </div>

                  {/* ── Is Default ── */}
                  <div className="form-group">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <input
                        type="checkbox"
                        id="isDefault"
                        name="isDefault"
                        checked={formData.isDefault}
                        onChange={handleInputChange}
                        style={{ width: 18, height: 18, cursor: 'pointer', accentColor: 'var(--color-primary)' }}
                      />
                      <label htmlFor="isDefault" className="form-label" style={{ margin: 0, cursor: 'pointer', fontWeight: 600 }}>
                        {t('paymentType.setAsDefault')}
                      </label>
                    </div>
                    <small className="form-helper">{t('paymentType.isDefaultHelper')}</small>
                  </div>

                  {/* ── Is Active ── */}
                  <div className="form-group">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <input
                        type="checkbox"
                        id="isActive"
                        name="isActive"
                        checked={formData.isActive}
                        onChange={handleInputChange}
                        style={{ width: 18, height: 18, cursor: 'pointer', accentColor: 'var(--color-primary)' }}
                      />
                      <label htmlFor="isActive" className="form-label" style={{ margin: 0, cursor: 'pointer', fontWeight: 600 }}>
                        {t('paymentType.isActive')}
                      </label>
                    </div>
                    <small className="form-helper">
                      {t('common.inactive')} payment types won't appear in invoices or POS.
                    </small>
                  </div>


                </form>
              </div>
            </div>
          </div>

          {/* ════ RIGHT — info sidebar ════ */}
          <div className="edit-info-section">

            {/* Payment type info card */}
            <div className="edit-card">
              <div className="edit-card-header">
                <h3 className="edit-card-title">{t('paymentType.details')}</h3>
              </div>
              <div className="edit-card-body">

                <div className="info-item">
                  <label className="info-label">{t('paymentType.code')}</label>
                  <div className="info-value detail-code" style={{ display: 'inline-block' }}>{formData.code}</div>
                </div>

                <div className="info-item">
                  <label className="info-label">{t('paymentType.usageScope', 'Usage Scope')}</label>
                  <div className="info-value">
                    <span className="info-badge info-badge-root">
                      {SCOPE_OPTIONS.find((o) => o.value === formData.usageScope)?.icon}{' '}
                      {formData.usageScope ?? 'Both'}
                    </span>
                  </div>
                </div>

                <div className="info-item">
                  <label className="info-label">{t('paymentType.paymentCategory')}</label>
                  <div className="info-value">
                    <span className={catBadgeCls[formData.paymentCategory] ?? 'info-badge info-badge-root'}>
                      {CATEGORY_OPTIONS.find((o) => o.value === formData.paymentCategory)?.icon}{' '}
                      {formData.paymentCategory}
                    </span>
                  </div>
                </div>

                <div className="info-item">
                  <label className="info-label">{t('common.status')}</label>
                  <div className="info-value">
                    <span className={formData.isActive ? 'info-badge info-badge-active' : 'info-badge info-badge-inactive'}>
                      {formData.isActive ? t('common.active') : t('common.inactive')}
                    </span>
                  </div>
                </div>

                <div className="info-item">
                  <label className="info-label">{t('paymentType.isDefault')}</label>
                  <div className="info-value">
                    {formData.isDefault
                      ? <span className="info-badge info-badge-sub">✓ {t('paymentType.default')}</span>
                      : <span style={{ color: '#9ca3af' }}>—</span>
                    }
                  </div>
                </div>

                <div className="info-item">
                  <label className="info-label">{t('paymentType.sortOrder')}</label>
                  <div className="info-value">{formData.sortOrder}</div>
                </div>

              </div>
            </div>

            {/* Help card */}
            <div className="edit-card">
              <div className="edit-card-header">
                <h3 className="edit-card-title">{t('common.info')}</h3>
              </div>
              <div className="edit-card-body">
                <div className="help-item">
                  <strong>💵 Cash</strong>
                  <p>Only one active Cash type is allowed. Enables the tendered/change calculator at POS.</p>
                </div>
                <div className="help-item">
                  <strong>🏦 Credit</strong>
                  <p>Only one active Credit type is allowed. Records the sale as an on-account receivable.</p>
                </div>
                <div className="help-item">
                  <strong>💳 Other</strong>
                  <p>Cards, vouchers, bank transfers, etc. Multiple Other types can exist.</p>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>
    </Layout>
  );
};

export default PaymentTypeEditPage;
